import React, { cloneElement, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import Chart from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import { adapterFactory } from "chartjs-adapter-date-fns";
import { useParams } from "react-router-dom";
import { Avatar, Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { ethers } from "ethers";
import moment from "moment-timezone";
import "react-tabs/style/react-tabs.css";
import NETWORKS from "../../networks";

import { truncateAddress } from "../../utils";
import History from "../History";

import { getTransactions } from "../../models/transactions";
import GAMES, {
  MODULO_COINFLIP,
  MODULO_ETHEROLL,
  MODULO_ROLLADICE,
  MODULO_ROULETTE,
  MODULO_TWODICE,
} from "../../games";
import { getUserByAddress } from "../../models/users";
import SessionContext from "../../contexts/session";

import VerifiedIcon from "@mui/icons-material/Verified";
import { useTranslation, Trans } from "react-i18next";
import { useContext } from "react";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function UserProfile() {
  const { selectedNetwork } = useContext(SessionContext);
  const { t, i18n } = useTranslation();
  const { address } = useParams();

  const [pageUser, setPageUser] = useState(null);

  const [stats, setStats] = useState(null);

  const [gamesPlayed, setGamesPlayed] = useState(0);
  const [gamesPlayedDict, setGamesPlayedDict] = useState([]);
  const [totalWageredDict, setTotalWageredDict] = useState([]);
  const [netProfitDict, setNetProfitDict] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [options, setOptions] = useState([]);  
  const [hasLoaded, setHasLoaded] = useState(false);
  //const [timezone, setTimezone] = useState("");
  const [valueTop, setValueTop] = useState("1");
  const [valueBottom, setValueBottom] = useState("1");

  function handleChangeTop(newValue) {
    setValueTop(newValue.currentTarget.id.split("-T-")[1]);
  }

  function handleChangeBottom(newValue) {
    setValueBottom(newValue.currentTarget.id.split("-T-")[1]);
  }

  useEffect(() => {
    setHasLoaded(false);
    let didCancel = false;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    //setTimezone(timezone);
    async function updateStats() {
      const [user, transactions] = await Promise.all([
        getUserByAddress(address),
        getTransactions(null, address),
      ]);
      if (!didCancel) {
        setPageUser(user);
        let totalWageredDict = {};
        let netProfitDict = {};
        Object.values(NETWORKS)
          .filter((network) => !network.disabled)
          .forEach((network) => {
            netProfitDict[network.chainIdDec] = ethers.BigNumber.from(0);
            totalWageredDict[network.chainIdDec] = ethers.BigNumber.from(0);
            gamesPlayedDict[network.chainIdDec] = 0;
          });
        let coinGamePlayed = 0;
        let diceGamePlayed = 0;
        let twoDiceGamePlayed = 0;
        let rouletteGamePlayed = 0;
        let etherollGamePlayed = 0;
        let gamesPlayed = 0;
        const lineData = {};
        const options = {};

        Object.values(NETWORKS)
          .filter((network) => !network.disabled)
          .forEach((network) => {
            lineData[network.chainIdDec] = {
              datasets: [
                {
                  label: network.ui.symbol + " "+ t("Profits"),
                  data: [],
                  fill: false,
                  borderColor: "#3498DB",
                },
              ],
            };

            options[network.chainIdDec] = {
              plugins: {
                legend: {
                  display: true, // Set the display property to false for the entire legend configuration
                  position: "bottom",
                  labels: {
                    color: "white",
                  },
                },
                zoom: {
                  zoom: {
                    wheel: {
                      enabled: true,
                    },
                    pinch: {
                      enabled: true,
                    },
                    mode: "xy",
                  },
                },
              },
              scales: {
                x: {
                  //grid: {
                  //color: 'white',
                  //borderColor: 'white'
                  //},
                  ticks: { color: "lightgray" },
                  type: "time",
                  time: {
                    unit: "month",
                    displayFormats: {
                      year: "yyyy",
                    },
                  },
                  title: {
                    display: true,
                    text: t("Time"),
                    color: "lightgray",
                  },
                },
                y: {
                  beginAtZero: true,
                  ticks: { color: "lightgray" },
                  title: {
                    display: true,
                    text: network.ui.symbol,
                    color: "lightgray",
                  },
                },
              },
            };
          });
        Chart.register(zoomPlugin);
        transactions
          .slice()
          .reverse()
          .forEach((transaction) => {
            totalWageredDict[transaction.chainId] = totalWageredDict[
              transaction.chainId
            ].add(ethers.BigNumber.from(transaction.betValue));
            if (transaction.isWinner) {
              netProfitDict[transaction.chainId] = netProfitDict[
                transaction.chainId
              ].add(ethers.BigNumber.from(transaction.wonValue));
            }
            netProfitDict[transaction.chainId] = netProfitDict[
              transaction.chainId
            ].sub(ethers.BigNumber.from(transaction.betValue));
            if (Number(transaction.modulo) === MODULO_COINFLIP) {
              coinGamePlayed++;
            } else if (Number(transaction.modulo) === MODULO_ROLLADICE) {
              diceGamePlayed++;
            } else if (Number(transaction.modulo) === MODULO_TWODICE) {
              twoDiceGamePlayed++;
            } else if (Number(transaction.modulo) === MODULO_ROULETTE) {
              rouletteGamePlayed++;
            } else if (Number(transaction.modulo) === MODULO_ETHEROLL) {
              etherollGamePlayed++;
            }
            gamesPlayed++;
            gamesPlayedDict[transaction.chainId] += 1;
            let currentDate = moment(transaction.createdAt);
            currentDate.tz(timezone);
            let stringDate = currentDate.format("YYYY-MM-DD HH:mm:ss");
            lineData[transaction.chainId].datasets[0].data.push({
              x: stringDate,
              y: ethers.utils.formatEther(netProfitDict[transaction.chainId]),
            });
          });
        setStats({
          [MODULO_COINFLIP]: coinGamePlayed,
          [MODULO_ROLLADICE]: diceGamePlayed,
          [MODULO_TWODICE]: twoDiceGamePlayed,
          [MODULO_ROULETTE]: rouletteGamePlayed,
          [MODULO_ETHEROLL]: etherollGamePlayed,
        });
        setGamesPlayed(gamesPlayed);
        Object.values(NETWORKS)
          .filter((network) => !network.disabled)
          .forEach((network) => {
            totalWageredDict[network.chainIdDec] = parseFloat(
              ethers.utils.formatEther(totalWageredDict[network.chainIdDec])
            ).toFixed(selectedNetwork.ui.decimals);
            netProfitDict[network.chainIdDec] = parseFloat(
              ethers.utils.formatEther(netProfitDict[network.chainIdDec])
            ).toFixed(selectedNetwork.ui.decimals);
          });
        setGamesPlayedDict(gamesPlayedDict);
        setTotalWageredDict(totalWageredDict);
        setNetProfitDict(netProfitDict);
        setOptions(options);
        setLineData(lineData);        
        setHasLoaded(true);
      }
    }
    updateStats();
    return () => {
      didCancel = true;
    };
  }, [address, gamesPlayedDict, selectedNetwork.ui.decimals,t]);
  const totalString = t("Total Games");
  const wageredString = t("Total Wagered");
  const netProfitString = t("Net Profit");
  if (hasLoaded) {
    return (
      <>
        <Stack
          component={Paper}
          spacing={4}
          sx={{ alignSelf: "center", p: 4, minWidth: "250px" }}
        >
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Avatar />
            {pageUser?.verified && (
              <Typography>
                {pageUser.username}{" "}
                <VerifiedIcon color="info" sx={{ verticalAlign: "middle" }} />
              </Typography>
            )}
            <Typography>{truncateAddress(address)}</Typography>
            {/* <Stack direction="row" sx={{ alignItems: "center" }}>
              <Button variant="contained" color="secondary">
                Follow
              </Button>
              <StarRateRoundedIcon sx={{ fill: "yellow" }} />
            </Stack> */}
          </Stack>
          <Divider />
          <Stack spacing={2}>
            <Typography color="secondary" sx={{ alignSelf: "center" }}>
              <Trans t={t}>General Stats</Trans>
            </Typography>
            <Typography>
              {totalString}: {gamesPlayed}
            </Typography>
            {Object.values(GAMES).forEach((game) => (
              <Typography key={game.modulo}>
                <Box
                  component={"img"}
                  width={24}
                  src={game.iconUrl}
                  sx={{ verticalAlign: "middle", mr: 1 }}
                />
                {t(game.name)}: {stats && stats[game.modulo]}
              </Typography>
            ))}
          </Stack>
          <Divider />
          {(() => {
            let jsxOrigine = <TabList onChange={handleChangeTop}></TabList>;            
            let jsxAdd = (
              <Tab
                icon={
                  <Box
                    component="img"
                    sx={{
                      mr: 1,
                      filter: "grayscale(100%)",
                      verticalAlign: "middle",
                    }}
                    src={selectedNetwork.ui.menuIcon}
                    height={36}
                  />
                }
                key={"tab-up" + selectedNetwork.chainIdDec.toString()}
                label={selectedNetwork.chainName}
                value="1"
              ></Tab>
            );
            jsxOrigine = React.cloneElement(jsxOrigine, null, jsxAdd);

            let updatedChildren = React.Children.toArray(
              jsxOrigine.props.children
            );

            let indexHelper = 1;
            Object.values(NETWORKS)
              .filter((network) => !network.disabled)
              .forEach((network, index) => {
                if (network.chainIdDec !== selectedNetwork.chainIdDec) {                                  
                  indexHelper += 1;
                  updatedChildren.push(
                    <Tab
                      icon={
                        <Box
                          component="img"
                          sx={{
                            mr: 1,
                            filter: "grayscale(100%)",
                            verticalAlign: "middle",
                          }}
                          src={network.ui.menuIcon}
                          height={36}
                        />
                      }
                      key={"tab-up" + network.chainIdDec.toString()}
                      label={network.chainName}
                      value={indexHelper.toString()}
                    />
                  );
                }
              });

            const jsxUpdated = React.cloneElement(
              jsxOrigine,
              null,
              ...updatedChildren
            );
            let tabContext = <></>;
            let panels = [];
            tabContext = React.cloneElement(tabContext, null, jsxUpdated);
            let jsxAdd2 = (
              <TabPanel
                value={(1).toString()}
                key={"tab-up-results" + selectedNetwork.chainIdDec.toString()}
              >
                <Stack spacing={2}>
                  <Typography>
                    {totalString}: {gamesPlayedDict[selectedNetwork.chainIdDec]}
                  </Typography>
                  <Typography>
                    {wageredString}:{" "}
                    {totalWageredDict[selectedNetwork.chainIdDec]}{" "}
                    {selectedNetwork.ui.symbol}
                  </Typography>
                  <Typography>
                    {netProfitString}: {netProfitDict[selectedNetwork.chainIdDec]}{" "}
                    {selectedNetwork.ui.symbol}
                  </Typography>
                </Stack>
              </TabPanel>
            );
            panels.push(jsxAdd2);
            let indexHelper2 = 1;
            // Create an array of new TabPanel elements to add
            Object.values(NETWORKS)
              .filter(
                (network) =>
                  !network.disabled &&
                  network.chainIdDec !== selectedNetwork.chainIdDec
              )
              .forEach((network, index) => {
                if (network.chainIdDec !== selectedNetwork.chainIdDec) {
                  indexHelper2 += 1;
                  panels.push(
                    <TabPanel
                      key={"tab-up-results" + network.chainIdDec.toString()}
                      value={indexHelper2.toString()}
                    >
                      <Stack spacing={2}>
                        <Typography>
                          {totalString}: {gamesPlayedDict[network.chainIdDec]}
                        </Typography>
                        <Typography>
                          {wageredString}: {totalWageredDict[network.chainIdDec]}{" "}
                          {network.ui.symbol}
                        </Typography>
                        <Typography>
                          {netProfitString}: {netProfitDict[network.chainIdDec]}{" "}
                          {network.ui.symbol}
                        </Typography>
                      </Stack>
                    </TabPanel>
                  );
                }
              });

            tabContext = cloneElement(
              tabContext,
              null,
              tabContext.props.children,
              panels
            );
            return (
              <TabContext value={valueTop.toString()}>
                {tabContext.props.children[0]}
                {tabContext.props.children[1]}
              </TabContext>
            );
          })()}
        </Stack>
        <Stack sx={{ alignSelf: "center", p: 4, minWidth: "50%" }}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography align="center" sx={{ width: "100%" }}>
              <Trans t={t}>Win/Loss Graph</Trans>📈
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Divider />
              {(() => {
                let jsxOrigine = (
                  <TabList onChange={handleChangeBottom} centered></TabList>
                );
                let jsxAdd = (
                  <Tab
                    icon={
                      <Box
                        component="img"
                        sx={{
                          mr: 1,
                          filter: "grayscale(100%)",
                          verticalAlign: "middle",
                        }}
                        src={selectedNetwork.ui.menuIcon}
                        height={36}
                      />
                    }
                    label={selectedNetwork.chainName}
                    value="1"
                    key={"tab-down" + selectedNetwork.chainIdDec.toString()}
                  ></Tab>
                );
                jsxOrigine = React.cloneElement(jsxOrigine, null, jsxAdd);
                let updatedChildren = React.Children.toArray(
                  jsxOrigine.props.children
                );
                let indexHelper = 1;
                Object.values(NETWORKS)
                  .filter((network) => !network.disabled)
                  .forEach((network, index) => {
                    if (network.chainIdDec !== selectedNetwork.chainIdDec) {
                      indexHelper += 1;                      
                      updatedChildren.push(
                        <Tab
                          icon={
                            <Box
                              component="img"
                              sx={{
                                mr: 1,
                                filter: "grayscale(100%)",
                                verticalAlign: "middle",
                              }}
                              src={network.ui.menuIcon}
                              height={36}
                            />
                          }
                          label={network.chainName}
                          value={indexHelper.toString()}
                          key={"tab-down" + network.chainIdDec.toString()}
                        ></Tab>
                      );
                    }
                  });

                const jsxUpdated = React.cloneElement(
                  jsxOrigine,
                  null,
                  ...updatedChildren
                );
                let tabContext = <></>;
                let panels = [];
                tabContext = React.cloneElement(tabContext, null, jsxUpdated);
                let jsxAdd2 = (
                  <TabPanel
                    value="1"
                    key={
                      "tab-down-results" + selectedNetwork.chainIdDec.toString()
                    }
                  >
                    <Line
                      options={options[selectedNetwork.chainIdDec]}
                      data={lineData[selectedNetwork.chainIdDec]}
                    />
                  </TabPanel>
                );
                panels.push(jsxAdd2);
                let indexHelper2 = 1;
                Object.values(NETWORKS)
                  .filter((network) => !network.disabled)
                  .forEach((network, index) => {
                    if (network.chainIdDec !== selectedNetwork.chainIdDec) {
                      indexHelper2 += 1;
                      panels.push(
                        <TabPanel
                          value={indexHelper2.toString()}
                          key={
                            "tab-down-results" + network.chainIdDec.toString()
                          }
                        >
                          <Line
                            options={options[network.chainIdDec]}
                            data={lineData[network.chainIdDec]}
                          />
                        </TabPanel>
                      );
                    }
                  });
                tabContext = cloneElement(
                  tabContext,
                  null,
                  tabContext.props.children,
                  panels
                );
                return (
                  <TabContext value={valueBottom.toString()}>
                    {tabContext.props.children[0]}
                    {tabContext.props.children[1]}
                  </TabContext>
                );
              })()}
            </AccordionDetails>
          </Accordion>
        </Stack>
        <History address={address} />
      </>
    );
  } else {
    return (
      <>
        <Stack
          component={Paper}
          spacing={4}
          sx={{ alignSelf: "center", p: 4, minWidth: "250px" }}
        >
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Avatar />
            {pageUser?.verified && (
              <Typography>
                {pageUser.username}{" "}
                <VerifiedIcon color="info" sx={{ verticalAlign: "middle" }} />
              </Typography>
            )}
            <Typography>{truncateAddress(address)}</Typography>
            {/* <Stack direction="row" sx={{ alignItems: "center" }}>
                      <Button variant="contained" color="secondary">
                        Follow
                      </Button>
                      <StarRateRoundedIcon sx={{ fill: "yellow" }} />
                    </Stack> */}
          </Stack>
          <Divider />
        </Stack>
        <History address={address} />
      </>
    );
  }
}
